import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { type TaskView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { boardApi, boardKeys } from '../../board/api';

/** Kiểu dữ liệu của cú kéo — đọc ở `onDrop` của Gantt. */
export const DRAG_TASK_MIME = 'application/x-apm-task';

/**
 * Ngăn kéo lập lịch — APM-BOARD-c.
 *
 * ## Vì sao gập được, không phải một tab riêng
 *
 * Lập lịch là việc **so sánh**: nhìn chỗ trống trên Gantt rồi tìm việc lấp vào.
 * Hai màn hình thì phải nhớ nửa kia trong đầu, và người ta sẽ kéo bừa việc trên
 * cùng danh sách vì đó là việc duy nhất còn nhớ tên.
 *
 * ## Vì sao dùng kéo thả **của trình duyệt**, không phải dnd-kit
 *
 * Gantt kéo thanh bằng pointer event thô (`onPointerDown` + `setPointerCapture`),
 * không qua dnd-kit. Dựng một `DndContext` bao cả hai sẽ để hai cơ chế cùng
 * giành một cú kéo — và triệu chứng là thanh nhảy lung tung khi người dùng chỉ
 * định kéo một dòng từ ngăn dưới.
 *
 * ## ⚠️ Kéo không phải đường duy nhất
 *
 * Mỗi dòng có nút "đưa vào sprint": kéo thả **chỉ có** với chuột, còn đây là
 * thao tác thật của quy trình chứ không phải một tiện ích.
 */
export function ScheduleDrawer({
  projectId,
  sprintName,
  canSchedule,
  onSendToSprint,
}: {
  readonly projectId: string;
  /** Sprint đang xem. `null` = chưa chọn ⇒ nút gửi vào sprint không có đích. */
  readonly sprintName: string | null;
  readonly canSchedule: boolean;
  readonly onSendToSprint: (task: TaskView) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pageCursor, setPageCursor] = useState({ projectId, offset: 0 });
  const offset = pageCursor.projectId === projectId ? pageCursor.offset : 0;

  const backlog = useQuery({
    queryKey: [...boardKeys.backlog(projectId), 'drawer-window', offset],
    queryFn: ({ signal }) =>
      boardApi.backlog(projectId, null, false, { offset, limit: 100 }, signal),
  });

  const tasks = backlog.data?.tasks ?? [];

  return (
    <section
      aria-label={t('scheduleDrawer.title')}
      className="mt-3 rounded-DEFAULT border border-border"
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40"
      >
        {open ? (
          <ChevronDown aria-hidden className="size-4" />
        ) : (
          <ChevronRight aria-hidden className="size-4" />
        )}
        <span className="font-medium">{t('scheduleDrawer.title')}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums">
          {backlog.data?.page?.fullTotal ?? tasks.length}
        </span>
        {/* Câu hướng dẫn nằm ở đầu, hiện cả khi đang gập: nó là lý do người ta
            mở ngăn này ra, nên giấu nó vào trong là giấu mất chính lời mời. */}
        <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
          {t('scheduleDrawer.hint')}
        </span>
      </button>

      {open ? (
        <div className="max-h-64 overflow-y-auto border-t border-border">
          {backlog.data?.page && backlog.data.page.total > 100 && (
            <div className="flex items-center justify-between gap-2 p-2 text-xs">
              <Button
                size="sm"
                variant="outline"
                disabled={backlog.data.page.offset === 0}
                onClick={() =>
                  setPageCursor({
                    projectId,
                    offset: Math.max(0, backlog.data!.page!.offset - 100),
                  })
                }
              >
                {t('common.previous')}
              </Button>
              <span>
                {backlog.data.page.offset + 1}–{backlog.data.page.offset + tasks.length} /{' '}
                {backlog.data.page.total}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={backlog.data.page.offset + 100 >= backlog.data.page.total}
                onClick={() =>
                  setPageCursor({ projectId, offset: backlog.data!.page!.offset + 100 })
                }
              >
                {t('common.next')}
              </Button>
            </div>
          )}
          {tasks.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              {t('scheduleDrawer.empty')}
            </p>
          ) : (
            <ul>
              {tasks.map((task) => (
                <Row
                  key={task.id}
                  task={task}
                  sprintName={sprintName}
                  canSchedule={canSchedule}
                  onSendToSprint={() => onSendToSprint(task)}
                />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

function Row({
  task,
  sprintName,
  canSchedule,
  onSendToSprint,
}: {
  readonly task: TaskView;
  readonly sprintName: string | null;
  readonly canSchedule: boolean;
  readonly onSendToSprint: () => void;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <li
      draggable={canSchedule}
      onDragStart={(event) => {
        // `text/plain` kèm theo vì Firefox bỏ qua cú kéo không có nó, và khi ấy
        // `onDrop` bên Gantt không bao giờ chạy.
        event.dataTransfer.setData(DRAG_TASK_MIME, task.id);
        event.dataTransfer.setData('text/plain', task.key);
        event.dataTransfer.effectAllowed = 'move';
      }}
      className={cn(
        'flex items-center gap-2 border-b border-border/60 px-3 py-1.5 text-sm last:border-b-0',
        canSchedule && 'cursor-grab hover:bg-muted/40',
      )}
    >
      {canSchedule ? (
        <GripVertical aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      ) : null}

      <span className="shrink-0 font-mono text-xs text-muted-foreground">{task.key}</span>
      <span className="min-w-0 flex-1 truncate">{task.title}</span>

      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {task.estimateHours === null
          ? t('scheduleDrawer.noEstimate')
          : t('scheduleDrawer.hours', { count: task.estimateHours })}
      </span>

      {canSchedule && sprintName !== null ? (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 shrink-0 px-2 text-xs"
          onClick={onSendToSprint}
        >
          {t('scheduleDrawer.sendToSprint', { name: sprintName })}
        </Button>
      ) : null}
    </li>
  );
}
